import _ from 'lodash';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveBar } from '@nivo/bar';
import {
  AXIS_BOTTOM_LEGEND,
  AXIS_TICK_OFFSET,
} from './constants';
import { GenerateDecoratedCustomChartBarsFC } from './CustomChartBars';
import { ChartCustomTick } from './ChartCustomTick';
import {
  Container,
  ContainerHeader,
  QuestionOrder,
  Title,
  ChartContainer,
  WatchQuestionLink,
  QuestionTitleContainer,
} from './styles';
import OtherWritten from './other-written';

const BAR_HEIGHT = 80; // fixed height bar
const PADDING = 20; // padding between bars

/**
 * VERTICAL CHART FOR ANALYTICS ONLY
 * UNMODERATED AND SURVEYS USES IT
 */

const VerticalChart = (props) => {
  const {
    title,
    color,
    data,
    showImage,
    showOtherWritten,
    projectId,
    projectTestType,
  } = props;
  const {
    id,
    options,
    label,
    questionOrder,
    total,
  } = data;

  const yAxisPossibleTicks = [];

  const maxTick = Math.ceil(total / 10) * 10;
  yAxisPossibleTicks.push(..._.range(10, maxTick + 1, 10));

  // add the total number of answers as the last tick if it's not already included
  if (!yAxisPossibleTicks.includes(total)) {
    yAxisPossibleTicks.push(total);
  }

  const totalBars = options.length; // total options
  const chartHeight = ((totalBars * BAR_HEIGHT) + (totalBars * PADDING)) + 80; // total chart height

  const chartData = useMemo(() => options.map((option) => ({
    id: option.id,
    value: `${option.count}`,
  })).reverse(), [options]);

  return (
    <Container>
      <ContainerHeader>
        <QuestionTitleContainer>
          <QuestionOrder>{`Question ${questionOrder + 1}`}</QuestionOrder>
          <Title>{`${label} (${title})`}</Title>
        </QuestionTitleContainer>

        {
          // unmoderated analytics
          projectTestType !== 'tree_test' && projectTestType !== 'survey' && (
            <WatchQuestionLink
              rel="internal"
              target="_blank"
              href={`/projects/${projectId}/videos/questions/${id}`}
            >
              {`Watch Question ${questionOrder + 1}`}
            </WatchQuestionLink>
          )
        }
      </ContainerHeader>
      <ChartContainer style={{ height: `${chartHeight}px` }}>
        <ResponsiveBar
          data={chartData}
          enableGridX={false}
          enableGridY={false}
          axisLeft={
            {
              renderTick: (tick) => {
                const {
                  x, y, lineY, value,
                } = tick;

                const option = options.find((item) => item.id === value);

                return (
                  <ChartCustomTick
                    label={option?.label}
                    imageLink={option?.imageUrl}
                    x={x}
                    y={y + lineY}
                    showImage={!!showImage}
                  />
                );
              },
            }
          }
          colors={[color]}
          axisBottom={
            {
              tickValues: yAxisPossibleTicks,
              legend: AXIS_BOTTOM_LEGEND,
              legendPosition: 'middle',
              legendOffset: 40,
              tickSize: 0,
            }
          }
          margin={{
            top: 0,
            right: 60,
            bottom: 50,
            left: AXIS_TICK_OFFSET,
          }}
          gridXValues={yAxisPossibleTicks}
          indexBy="id"
          layout="horizontal"
          labelSkipWidth={6}
          layers={
            [
              'grid',
              'axes',
              GenerateDecoratedCustomChartBarsFC({
                ...props,
                totalAnswerCount: total,
              }),
            ]
          }
          tooltip={() => <></>}
          maxValue={total}
          animate
          theme={
            {
              axis: {
                domain: {
                  line: {
                    strokeWidth: 1,
                    stroke: '#777777',
                  },
                },
              },
            }
          }
        />
      </ChartContainer>

      {
        showOtherWritten && (
          <OtherWritten
            others={data.others}
          />
        )
      }
    </Container>
  );
};

VerticalChart.defaultProps = {
  showImage: false,
  showOtherWritten: false,
  projectId: '',
};

// props validation
VerticalChart.propTypes = {
  color: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  showImage: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.shape).isRequired,
  showOtherWritten: PropTypes.bool,
  projectId: PropTypes.string,
  projectTestType: PropTypes.string.isRequired,
};

export default VerticalChart;
